import { motion, useReducedMotion, type Transition } from "motion/react";
import { motionTokens } from "@/lib/motion";
import { cn } from "@/lib/utils";

export type MazuState = "idle" | "listen" | "think" | "done" | "error";

const copy: Record<MazuState, string> = {
  idle: "Mazu is here",
  listen: "Mazu is listening",
  think: "Mazu is thinking",
  done: "Mazu is done",
  error: "Mazu hit a problem",
};

type Eye = { x: number; y: number; scaleX: number; scaleY: number; rotate: number };
type Mouth = "smile" | "grin" | "flat" | "frown";
type Pose = {
  left: Eye;
  right: Eye;
  /** Open coral eyes, or closed happy arcs. */
  eyes: "open" | "happy";
  /** Outward tilt of each ear, in degrees. */
  ears: { left: number; right: number };
  mouth: Mouth;
};

const eye = (overrides: Partial<Eye> = {}): Eye => ({ x: 0, y: 0, scaleX: 1, scaleY: 1, rotate: 0, ...overrides });

/**
 * One still pose per state. Reduced motion shows exactly these, so every
 * state stays readable without a loop or a shake.
 * The eyes and ears must not use `initial={false}`: it jumps keyframe loops
 * to their last frame on mount and the loop never starts.
 */
const mazuPoses: Record<MazuState, Pose> = {
  idle: { left: eye(), right: eye(), eyes: "open", ears: { left: 0, right: 0 }, mouth: "smile" },
  listen: { left: eye({ scaleX: 1.12, scaleY: 1.12 }), right: eye({ scaleX: 1.12, scaleY: 1.12 }), eyes: "open", ears: { left: 10, right: 10 }, mouth: "smile" },
  think: { left: eye({ x: 2.5, y: -2.5, scaleX: 0.85, scaleY: 0.85 }), right: eye({ x: 2.5, y: -2.5, scaleX: 0.85, scaleY: 0.85 }), eyes: "open", ears: { left: -6, right: 12 }, mouth: "flat" },
  done: { left: eye(), right: eye(), eyes: "happy", ears: { left: 6, right: 6 }, mouth: "grin" },
  error: { left: eye({ scaleY: 0.55, rotate: 18 }), right: eye({ scaleY: 0.55, rotate: -18 }), eyes: "open", ears: { left: -10, right: -10 }, mouth: "frown" },
};

/*
 * Geometry follows the Mazu master kit (white head with a grey line, ears,
 * side pods, black mask, coral eyes). Brand colors come from the --mazu-*
 * tokens and stay the same in both themes.
 */
const EYE_Y = 33;
const EYES = [
  { cx: 22.5, side: "left" },
  { cx: 41.5, side: "right" },
] as const;
const EARS = [
  { x: 12.5, base: -24, side: "left", out: -1 },
  { x: 42.5, base: 24, side: "right", out: 1 },
] as const;
const MOUTHS: Record<Mouth, { d: string; filled?: boolean }> = {
  smile: { d: "M28.6 49.4c1.8 1.6 5 1.6 6.8 0" },
  grin: { d: "M27.6 48.4h8.8c0 2.7-2 4.3-4.4 4.3s-4.4-1.6-4.4-4.3z", filled: true },
  flat: { d: "M29 49.8h6" },
  frown: { d: "M28.6 51c1.8-1.6 5-1.6 6.8 0" },
};
const MASK =
  "M32 26.5C36.5 22.5 46 21.5 50 24.5 53.6 27.2 53 36.5 49 40.8 45.6 44.4 39.8 44.3 36 42.2L33.2 47.6 31.6 42.4C27.6 44.4 19 45 15 40.8 11 36.5 10.4 27.2 14 24.5 18 21.5 27.5 22.5 32 26.5Z";

export function Mazu({
  state = "idle",
  size = 64,
  className,
}: {
  state?: MazuState;
  /** Rendered size in px. 64 and 32 are the designed sizes. */
  size?: number;
  className?: string;
}) {
  const reduce = useReducedMotion();
  const pose = mazuPoses[state];
  const settle: Transition = reduce ? { duration: 0 } : motionTokens.spring.snappy;
  const blink = !reduce && state === "idle" && pose.eyes === "open";
  const scan = !reduce && state === "think";
  const perk = !reduce && state === "listen";

  return (
    <motion.div
      role="img"
      aria-label={copy[state]}
      data-state={state}
      data-slot="mazu"
      className={cn("inline-flex shrink-0", className)}
      style={{ width: size, height: size }}
      initial={false}
      animate={{ x: !reduce && state === "error" ? [0, -2, 2, -1, 0] : 0 }}
      transition={{ duration: reduce ? 0 : motionTokens.duration.considered, ease: motionTokens.ease.inOut }}
    >
      <svg viewBox="0 0 64 64" width={size} height={size} aria-hidden>
        <g stroke="var(--mazu-line)" strokeWidth="1.6" fill="var(--mazu-body)">
          {EARS.map(({ x, base, side, out }) => {
            const tilt = pose.ears[side] * out;
            return (
              <g key={side} transform={`rotate(${base} ${x + 4.5} 19)`}>
                <motion.g
                  style={{ originX: 0.5, originY: 1 }}
                  animate={{ rotate: perk ? [0, tilt, tilt * 0.4, tilt] : tilt }}
                  transition={perk ? { duration: 1.6, repeat: Infinity, ease: "easeInOut" } : settle}
                >
                  <rect x={x} y="7" width="9" height="13" rx="4.5" />
                  <circle cx={x + 4.5} cy="11.5" r="2.1" />
                </motion.g>
              </g>
            );
          })}
          <rect x="4.5" y="27" width="7" height="16" rx="3.5" />
          <rect x="52.5" y="27" width="7" height="16" rx="3.5" />
          <ellipse cx="32" cy="35.5" rx="22" ry="20" />
        </g>
        <path d={MASK} fill="var(--mazu-ink)" />
        {EYES.map(({ cx, side }) => {
          const target = pose[side];
          return (
            <motion.g
              key={side}
              style={{ originX: 0.5, originY: 0.5 }}
              animate={
                blink
                  ? { ...target, scaleY: [1, 1, 0.12, 1] }
                  : scan
                    ? { ...target, x: [target.x, -target.x, target.x] }
                    : target
              }
              transition={
                blink
                  ? { duration: 4.2, repeat: Infinity, times: [0, 0.92, 0.96, 1] }
                  : scan
                    ? { duration: 2.4, repeat: Infinity, ease: "easeInOut" }
                    : settle
              }
            >
              <motion.g initial={false} animate={{ opacity: pose.eyes === "open" ? 1 : 0 }} transition={settle}>
                <circle cx={cx} cy={EYE_Y} r="5.4" fill="var(--mazu-coral)" />
                <circle cx={cx - 1.1} cy={EYE_Y + 1.4} r="2.7" fill="var(--mazu-coral-light)" />
                <circle cx={cx + 1.7} cy={EYE_Y - 1.8} r="1.45" fill="var(--mazu-body)" />
              </motion.g>
              <motion.path
                d={`M${cx - 4.4} ${EYE_Y + 2}q4.4-5.6 8.8 0`}
                fill="none"
                stroke="var(--mazu-coral)"
                strokeWidth="2.6"
                strokeLinecap="round"
                initial={false}
                animate={{ opacity: pose.eyes === "happy" ? 1 : 0 }}
                transition={settle}
              />
            </motion.g>
          );
        })}
        {(Object.keys(MOUTHS) as Mouth[]).map((mouth) => {
          const { d, filled } = MOUTHS[mouth];
          return (
            <motion.path
              key={mouth}
              d={d}
              fill={filled ? "var(--mazu-ink)" : "none"}
              stroke="var(--mazu-ink)"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={false}
              animate={{ opacity: pose.mouth === mouth ? 1 : 0 }}
              transition={settle}
            />
          );
        })}
      </svg>
    </motion.div>
  );
}
